import { cx } from '@linaria/core';
import { useState, useEffect, useId, useRef } from 'react';
import { AutoFocusInside } from 'react-focus-on';
import { TransitionGroup } from 'react-transition-group';
import { useComponentFrameworkState } from '@provider/component-framework';
import { resets } from '@ui/css';
import { IsomorphicPortal, CSSRefTransition } from '@utils/react';
import { wrapper, modalTransitionDurationMs, modal, closeButton, speechButton, scrollContainer, backdrop } from './modal.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { CrossIcon } from '../../icons/cross.js';
import { TransitionSafeFocusOn } from '../containers/focus-on.js';
import { Tooltip } from './tooltip/tooltip.js';
import { SpeechButton } from '../speech/speech-button.js';

function Modal(props) {
  // We keep an internal replica of the active state which will be updated just
  // after the actual active state.
  // This ensures that the active prop can be propagated to the ModalContent
  // before either enter/exit persition by react-transition-group begins.
  //
  // Doing this means that we can disable react-focus-on before retaining the
  // modal for x milliseconds.
  const [internalActive, setInternalActive] = useState(props.active);
  const targetSelector = useComponentFrameworkState(state => state.overlayContainerSelector);
  const target = targetSelector && document.querySelector(targetSelector) || undefined;
  useEffect(() => {
    setInternalActive(props.active);
  }, [props.active]);
  return /*#__PURE__*/jsx(IsomorphicPortal, {
    target: target,
    children: /*#__PURE__*/jsx(TransitionGroup, {
      component: null,
      children: internalActive && /*#__PURE__*/jsx(CSSRefTransition, {
        timeout: {
          enter: modalTransitionDurationMs.enter,
          exit: modalTransitionDurationMs.exit + (props.retainForMs ?? 0)
        },
        classNames: wrapper,
        children: ref => /*#__PURE__*/jsx(ModalContent, {
          ...props,
          ref: ref
        })
      })
    })
  });
}
function ModalContent(props) {
  const id = useId();
  const {
    shouldAutoFocusContent = true,
    includeSpeechButton = true
  } = props;
  const scrollContainerRef = useRef(null);
  return /*#__PURE__*/jsx("dialog", {
    ref: props.ref,
    open: true,
    className: cx(resets.rootCls, wrapper, props.className),
    children: /*#__PURE__*/jsxs(TransitionSafeFocusOn, {
      transitionEnterDuration: modalTransitionDurationMs.enter,
      enabled: typeof props.active === "undefined" || props.active,
      onEscapeKey: props.requiresInteraction ? undefined : props.onClose,
      onClickOutside: props.requiresInteraction ? undefined : props.onClose,
      scrollLock: true,
      focusLock: true,
      allowPinchZoom: true,
      autoFocus: true,
      returnFocus: {
        preventScroll: true
      },
      preventScrollOnFocus: true,
      children: [/*#__PURE__*/jsxs("div", {
        tabIndex: 0,
        id: id,
        className: modal,
        children: [!props.requiresInteraction && /*#__PURE__*/jsx(Tooltip, {
          body: props.closeButtonLabel ?? "Luk",
          placement: "bottom-start",
          ariaType: "label",
          children: ref => /*#__PURE__*/jsx("button", {
            ref: ref,
            className: closeButton,
            "data-theme": props.theme,
            onClick: props.onClose,
            children: /*#__PURE__*/jsx(CrossIcon, {
              height: "13"
            })
          })
        }), includeSpeechButton && /*#__PURE__*/jsx(SpeechButton, {
          wrapperClassName: speechButton,
          rootSelector: `.${modal}[id="${id}"]`,
          hideWhenEmpty: true,
          defaultTooltip: "Læs op",
          scrollContainerRef: scrollContainerRef,
          withoutGuideBubble: true
        }), props.contentOutsideScrollcontainer, shouldAutoFocusContent ? /*#__PURE__*/jsx(AutoFocusInside, {
          children: /*#__PURE__*/jsx("div", {
            className: scrollContainer,
            ref: scrollContainerRef,
            children: props.children
          })
        }) : /*#__PURE__*/jsx("div", {
          className: scrollContainer,
          ref: scrollContainerRef,
          children: props.children
        })]
      }), !props.noBackdrop &&
      /*#__PURE__*/
      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
      jsx("div", {
        className: backdrop,
        onClick: props.onClose
      })]
    })
  });
}

export { Modal, ModalContent };
//# sourceMappingURL=modal.js.map
